"use client";

import { useEffect, useRef, useState } from "react";
import { track } from "@/lib/track";
import { api, setToken } from "@/lib/api";
import { referralParams } from "@/lib/referralAttribution";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
            [k: string]: unknown;
          }) => void;
          renderButton: (
            el: HTMLElement,
            options: Record<string, unknown>,
          ) => void;
        };
      };
    };
  }
}

const SCRIPT_SRC = "https://accounts.google.com/gsi/client";
// Google draws its button between 200 and 400 px wide.
const MIN_W = 200;
const MAX_W = 400;
const BTN_H = 48; // our button height in px

/** Loads Google's script once per page, however many times the button mounts. */
let scriptPromise: Promise<void> | null = null;
function loadGoogle(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    const s = existing ?? document.createElement("script");
    s.addEventListener("load", () => resolve());
    s.addEventListener("error", () => {
      scriptPromise = null;
      reject(new Error("Google Sign-In didn't load."));
    });
    if (!existing) {
      s.src = SCRIPT_SRC;
      s.async = true;
      document.head.appendChild(s);
    }
  });
  return scriptPromise;
}

/**
 * Google's own button, as wide as the form's inputs.
 *
 * It is drawn ONCE (and again only when the column's width changes). Earlier
 * the callbacks were effect dependencies; the pages pass new arrow functions on
 * every render, so each keystroke re-initialised Google and re-drew the button,
 * which flickered between "Sign in with Google" and "Sign in as …" and briefly
 * showed both. The latest callbacks now live in refs instead.
 */
export function GoogleSignInButton({
  onSuccess,
  onError,
  text = "continue_with",
  label,
  exchange,
}: {
  onSuccess: () => void;
  onError: (msg: string) => void;
  /** Google's wording: "signin_with", "signup_with" or "continue_with". */
  text?: "signin_with" | "signup_with" | "continue_with";
  /** Words shown on our own button; Google's real button sits invisibly on top to take the click. */
  label?: string;
  /** Override the default customer sign-in: trade the Google credential for a session yourself
   *  (e.g. the affiliate portal stores an affiliate token instead). Default = the customer flow. */
  exchange?: (credential: string) => Promise<void>;
}) {
  const box = useRef<HTMLDivElement>(null);
  const target = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const exchangeRef = useRef(exchange);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    exchangeRef.current = exchange;
  });

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    let drawnWidth = 0;
    let ro: ResizeObserver | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const draw = () => {
      const g = window.google?.accounts?.id;
      if (!g || !box.current || !target.current) return;
      // Google only draws 200-400px wide. Draw it at a safe width, then stretch the
      // (invisible) iframe with a transform so it covers our whole button.
      const full = box.current.clientWidth;
      const w = Math.floor(Math.max(MIN_W, Math.min(MAX_W, full)));
      if (w === drawnWidth) return;
      drawnWidth = w;
      target.current.innerHTML = "";
      target.current.style.transform = `scale(${full / w}, ${BTN_H / 40})`;
      g.renderButton(target.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        shape: "pill",
        text,
        logo_alignment: "center",
        width: w,
      });
    };

    loadGoogle()
      .then(() => {
        if (cancelled || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          ux_mode: "popup",
          callback: async (resp) => {
            try {
              if (exchangeRef.current) {
                await exchangeRef.current(resp.credential);
              } else {
                const { access_token } = await api.googleLogin(resp.credential, referralParams());
                setToken(access_token);
                track("login", { method: "google" });
              }
              onSuccessRef.current();
            } catch (err) {
              onErrorRef.current(
                err instanceof Error ? err.message : "Google sign-in failed.",
              );
            }
          },
        });
        draw();
        setState("ready");
        if (box.current && "ResizeObserver" in window) {
          ro = new ResizeObserver(() => {
            clearTimeout(timer);
            timer = setTimeout(draw, 150);
          });
          ro.observe(box.current);
        }
      })
      .catch(() => !cancelled && setState("failed"));

    return () => {
      cancelled = true;
      clearTimeout(timer);
      ro?.disconnect();
    };
  }, [clientId, text]);

  if (!clientId) return null;

  const words =
    label ??
    (text === "signup_with"
      ? "Sign up with Google"
      : text === "signin_with"
        ? "Sign in with Google"
        : "Continue with Google");

  return (
    <div>
      {/* Our own button is what people see, so nothing Google draws can overflow.
          Google's real button is stretched over it, invisible, and receives the click. */}
      <div
        ref={box}
        className="group relative h-12 w-full overflow-hidden rounded-2xl border border-[#e1deef] bg-white shadow-[0_1px_2px_rgba(23,20,43,0.04)] transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-px hover:border-[#c9c0f5] hover:shadow-[0_10px_24px_-14px_rgba(106,82,255,0.55)]"
      >
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2.5 text-[14.5px] font-bold text-[#17142b]">
          {state === "loading" ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#6a52ff]/30 border-t-[#6a52ff]" />
          ) : (
            <GoogleG />
          )}
          {words}
        </span>
        <div
          ref={target}
          aria-label={words}
          className={`absolute inset-0 flex items-center justify-center overflow-hidden opacity-[0.01] ${
            state === "ready" ? "" : "pointer-events-none"
          }`}
          style={{ transformOrigin: "center" }}
        />
      </div>
      {state === "failed" && (
        <p className="mt-2 text-center text-[0.78rem] font-semibold text-[#8d89a6]">
          Google sign-in is unavailable right now. Use email below.
        </p>
      )}
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path
        fill="#EA4335"
        d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z"
      />
      <path
        fill="#FBBC05"
        d="M10.5 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.9-6.1C.9 16.4 0 20.100 0 24s.9 7.600 2.600 10.800l7.900-6.100z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.500 0 11.900-2.100 15.900-5.800l-7.600-5.900c-2.100 1.400-4.900 2.300-8.300 2.300-6.300 0-11.600-4.100-13.500-9.800l-7.900 6.100C6.500 42.600 14.600 48 24 48z"
      />
    </svg>
  );
}
